import React, { useCallback, useEffect, useState } from 'react';
import { fetchSipTrunkSettings, saveSipTrunkSettings } from '../services/api';

const SECTIONS = [
  {
    key: 'local',
    title: 'Local calls',
    note: 'Regular Bangladesh numbers and free IPTSP numbers. Short codes use this gateway too, but are never cut or prefixed.',
  },
  {
    key: 'international',
    title: 'International calls',
    note: 'Every international destination is sent to this gateway.',
  },
];

const AFFIX_PATTERN = /^[0-9+*#]{0,20}$/;
const MAX_CUT = 15;

export const isValidGateway = (value) => {
  const match = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})(?::(\d{1,5}))?$/.exec(value.trim());
  if (!match) {
    return false;
  }
  if (match.slice(1, 5).some((octet) => Number(octet) > 255)) {
    return false;
  }
  return match[5] === undefined || (Number(match[5]) >= 1 && Number(match[5]) <= 65535);
};

const toFormSide = (side) => ({
  gateway_host: side?.gateway_host || '',
  caller_cut: String(side?.caller_cut ?? 0),
  caller_add: side?.caller_add || '',
  dest_cut: String(side?.dest_cut ?? 0),
  dest_add: side?.dest_add || '',
});

const toForm = (settings) => ({
  local: toFormSide(settings?.local),
  international: toFormSide(settings?.international),
});

const validate = (form) => {
  for (const { key, title } of SECTIONS) {
    const side = form[key];
    if (!isValidGateway(side.gateway_host)) {
      return `${title}: the gateway must be an IPv4 address, optionally with a port (for example 10.0.0.5 or 10.0.0.5:5080).`;
    }
    for (const field of ['caller_cut', 'dest_cut']) {
      if (!/^\d+$/.test(side[field]) || Number(side[field]) > MAX_CUT) {
        return `${title}: cut digit must be a whole number from 0 to ${MAX_CUT}.`;
      }
    }
    for (const field of ['caller_add', 'dest_add']) {
      if (!AFFIX_PATTERN.test(side[field])) {
        return `${title}: a prefix may only contain digits and + * # (up to 20 characters).`;
      }
    }
  }
  return null;
};

const toPayload = (form) => {
  const payload = {};
  for (const { key } of SECTIONS) {
    const side = form[key];
    payload[key] = {
      gateway_host: side.gateway_host.trim(),
      caller_cut: Number(side.caller_cut),
      caller_add: side.caller_add,
      dest_cut: Number(side.dest_cut),
      dest_add: side.dest_add,
    };
  }
  return payload;
};

const SipTrunks = () => {
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(toForm(null));
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    setMessage(null);

    try {
      const settings = await fetchSipTrunkSettings();
      setCurrent(settings);
      setForm(toForm(settings));
    } catch (error) {
      setCurrent(null);
      setMessage({
        type: 'error',
        text: error.message || 'Unable to load the SIP trunk settings.',
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const setField = (sectionKey, field, value) => {
    setForm((previous) => ({
      ...previous,
      [sectionKey]: { ...previous[sectionKey], [field]: value },
    }));
    setMessage(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage(null);

    const problem = validate(form);
    if (problem) {
      setMessage({ type: 'error', text: problem });
      return;
    }

    setIsSaving(true);

    try {
      const result = await saveSipTrunkSettings(toPayload(form));
      setMessage({
        type: result.data?.instances_applied === false ? 'info' : 'success',
        text: result.message || 'SIP trunk settings saved.',
      });

      try {
        const settings = await fetchSipTrunkSettings();
        setCurrent(settings);
        setForm(toForm(settings));
      } catch (refreshError) {
        console.error('Unable to refresh the SIP trunk settings:', refreshError);
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message || 'Unable to save the SIP trunk settings.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const busy = isLoading || isSaving;

  return (
    <div className="content">
      <main className="main-content sip-trunks-page">
        <h1 className="page-title">SIP Trunks</h1>

        <section className="sip-trunk-card" aria-labelledby="current-sip-trunk-title">
          <div>
            <p className="sip-trunk-eyebrow" id="current-sip-trunk-title">
              Current gateways
            </p>
            {isLoading ? (
              <p className="sip-trunk-loading">Loading current settings...</p>
            ) : !current ? (
              <p className="sip-trunk-unavailable">Unavailable</p>
            ) : current.configured ? (
              <>
                <p className="sip-trunk-current-value">Local: {current.local?.gateway_host}</p>
                <p className="sip-trunk-current-value">International: {current.international?.gateway_host}</p>
              </>
            ) : (
              <p className="sip-trunk-unavailable">
                Not saved yet. All calls go to {current.local?.gateway_host} with no cut or prefix.
                Saving below sets a gateway for each call type.
              </p>
            )}
          </div>

          <button
            type="button"
            className="sip-trunk-refresh-button"
            onClick={loadSettings}
            disabled={busy}
          >
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </section>

        <section className="sip-trunk-card sip-trunk-selection" aria-labelledby="sip-trunk-settings-title">
          <div className="sip-trunk-section-heading">
            <h2 id="sip-trunk-settings-title">Gateway settings</h2>
            <p>
              Each call type has its own gateway. Numbers are first put in a standard form: Bangladesh numbers
              and the caller number start with 880 (for example 8801673...), international numbers start with the
              country code (for example 61480...). Cut digit then removes that many digits from the start, and
              add prefix is put in front. 0 and an empty prefix leave the number as it is.
            </p>
          </div>

          {message && (
            <div
              className={`sip-trunk-message sip-trunk-message-${message.type}`}
              role={message.type === 'error' ? 'alert' : 'status'}
            >
              {message.text}
            </div>
          )}

          <form className="sip-trunk-settings-form" onSubmit={handleSubmit}>
            {SECTIONS.map(({ key, title, note }) => {
              const side = form[key];
              const name = key;
              return (
                <fieldset className="sip-trunk-fieldset" key={key} disabled={busy || !current}>
                  <legend>{title}</legend>
                  <p className="sip-trunk-fieldset-note">{note}</p>

                  <div className="sip-trunk-grid">
                    <div className="sip-trunk-field">
                      <label htmlFor={`${key}-gateway`}>{`Gateway for ${name} calls`}</label>
                      <input
                        id={`${key}-gateway`}
                        type="text"
                        value={side.gateway_host}
                        placeholder="e.g. 103.248.12.227"
                        onChange={(event) => setField(key, 'gateway_host', event.target.value)}
                      />
                    </div>
                    <div />

                    <div className="sip-trunk-field">
                      <label htmlFor={`${key}-caller-cut`}>{`Cut digit for ${name} caller number`}</label>
                      <input
                        id={`${key}-caller-cut`}
                        type="number"
                        min="0"
                        max={MAX_CUT}
                        value={side.caller_cut}
                        onChange={(event) => setField(key, 'caller_cut', event.target.value)}
                      />
                      <span className="sip-trunk-hint">Digits removed from the start of the caller number.</span>
                    </div>
                    <div className="sip-trunk-field">
                      <label htmlFor={`${key}-caller-add`}>{`Add prefix to ${name} caller number`}</label>
                      <input
                        id={`${key}-caller-add`}
                        type="text"
                        value={side.caller_add}
                        onChange={(event) => setField(key, 'caller_add', event.target.value)}
                      />
                      <span className="sip-trunk-hint">Added after the cut. Leave empty for none.</span>
                    </div>

                    <div className="sip-trunk-field">
                      <label htmlFor={`${key}-dest-cut`}>{`Cut digit for ${name} destination number`}</label>
                      <input
                        id={`${key}-dest-cut`}
                        type="number"
                        min="0"
                        max={MAX_CUT}
                        value={side.dest_cut}
                        onChange={(event) => setField(key, 'dest_cut', event.target.value)}
                      />
                      <span className="sip-trunk-hint">Digits removed from the start of the dialled number.</span>
                    </div>
                    <div className="sip-trunk-field">
                      <label htmlFor={`${key}-dest-add`}>{`Add prefix to ${name} destination number`}</label>
                      <input
                        id={`${key}-dest-add`}
                        type="text"
                        value={side.dest_add}
                        onChange={(event) => setField(key, 'dest_add', event.target.value)}
                      />
                      <span className="sip-trunk-hint">Added after the cut. Leave empty for none.</span>
                    </div>
                  </div>
                </fieldset>
              );
            })}

            <div className="sip-trunk-actions">
              <button type="submit" className="sip-trunk-submit-button" disabled={busy || !current}>
                {isSaving ? 'Saving...' : 'Save settings'}
              </button>
            </div>
          </form>
        </section>
      </main>
    </div>
  );
};

export default SipTrunks;
